import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faFilePdf,
  faFileWord,
  faTrashCan,
} from "@fortawesome/free-regular-svg-icons";
import { faBuildingCircleXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PopoverMenu, SmartPopoverMenu } from "../../../../../components";
import { cn } from "../../../../../components/utils/cn";
import { useStatsigClient } from "../../../../../hooks";
import { UserProfile } from "../../../../../client";
import { toast } from "sonner";
import { downloadProfileAsWord } from "../../../../../requests";
import { dialogsState } from "../../../../../state/dialogs";

interface Props {
  onArchive: () => void;
  onDelete?: () => void;
  leadId?: string;
  profileLinkId?: string;
  archived?: boolean;
  profiles: UserProfile[];
  profileId?: string;
}

export const LeadMenu = ({
  onArchive,
  onDelete,
  leadId,
  profileLinkId,
  archived,
  profiles,
  profileId,
}: Props) => {
  const statsigClient = useStatsigClient();

  const profileName = () => {
    if (
      profileId &&
      profiles.filter((p) => p.profileId === profileId).length > 0
    ) {
      let profileData = profiles.filter((p) => p.profileId === profileId)[0]
        .profileData;
      if (profileData) {
        return typeof profileData.name === "string"
          ? profileData.name
          : "member";
      }
    }

    return "Member";
  };
  const handlePdfDownload = async () => {
    // Log the PDF download event
    statsigClient?.logEvent("profile_pdf_download", undefined, {
      leadId: leadId ?? "",
      profileLinkId: profileLinkId ?? "",
    });

    var pdfUrl = "https://profile-pdf-exporter.onrender.com";
    var mainUrl = "https://voc.studio";
    /*
    if (
      window.location.origin.indexOf("localhost") !== -1
    ) {
      pdfUrl = "http://localhost:4000";
    }
     */

    const downloadUrl = pdfUrl + `/render?url=${mainUrl}/pdf/${profileLinkId}`;

    let name = profileName();
    const toastId = toast.loading(`Downloading PDF...`);

    try {
      const response = await fetch(downloadUrl, {
        method: "GET",
        headers: {
          Accept: "application/pdf",
          Name: name,
          x_api_key: process.env.REACT_APP_X_API_KEY ?? "",
          // Add any custom headers here if needed
          // 'Authorization': 'Bearer your-token',
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${name}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        console.error(
          "PDF download failed:",
          response.status,
          response.statusText,
        );
      }
    } catch (error) {
      console.error("PDF download error:", error);
    }

    toast.success(`PDF downloaded successfully!`, {
      id: toastId,
    });
  };

  if (!leadId && onDelete && archived === false) {
    return (
      <SmartPopoverMenu>
        <LeadMenuRow icon={faTrashCan} text="Delete" onClick={onDelete} />
      </SmartPopoverMenu>
    );
  }

  if (archived === true && onDelete) {
    return (
      <SmartPopoverMenu>
        <LeadMenuRow
          icon={faBuildingCircleXmark}
          text="Reopen lead"
          onClick={onArchive}
        />
        <LeadMenuRow icon={faTrashCan} text="Delete" onClick={onDelete} />
      </SmartPopoverMenu>
    );
  }

  return (
    <SmartPopoverMenu>
      {profileLinkId ? (
        <>
          <LeadMenuRow
            icon={faFilePdf}
            text="Download as PDF"
            onClick={handlePdfDownload}
          />
          {statsigClient?.checkGate("word_export") && (
            <LeadMenuRow
              icon={faFileWord}
              text="Download as Word"
              onClick={() => {
                if (
                  localStorage.getItem("word_export_info_displayed") === "true"
                ) {
                  const toastId = toast.loading(`Downloading Word document...`);
                  downloadProfileAsWord(profileLinkId)
                    .then(() => {
                      toast.success(`Word document downloaded successfully!`, {
                        id: toastId,
                      });
                    })
                    .catch(() => console.log("Download failed"));
                } else {
                  localStorage.setItem("word_export_info_displayed", "true");
                  dialogsState.showWordExportInfo = true;
                  dialogsState.showWordExportInfoProfileId = profileLinkId;
                }
              }}
            />
          )}
        </>
      ) : null}
      <LeadMenuRow
        icon={faBuildingCircleXmark}
        text={profileId ? "Archive Profile Version" : "Archive lead"}
        onClick={onArchive}
      />
    </SmartPopoverMenu>
  );
};

interface LeadMenuRowProps {
  onClick: () => void;
  icon: IconDefinition;
  text: string;
  iconClassName?: string;
}

const LeadMenuRow = ({
  icon,
  text,
  onClick,
  iconClassName,
}: LeadMenuRowProps) => (
  <SmartPopoverMenu.Item onClick={onClick}>
    <FontAwesomeIcon
      width={14}
      height={14}
      icon={icon}
      className={cn("shrink-0", iconClassName)}
    />
    <span className="text-sm text-black">{text}</span>
  </SmartPopoverMenu.Item>
);

LeadMenu.displayName = "LeadMenu";
